import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useCart } from '@/lib/cart';
import { Card, CardContent } from '@/components/ui/card';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Plus, Minus, Trash2, Search, ShoppingBag, ChevronRight, Clock, Leaf, AlertCircle, Tag, Info } from 'lucide-react';
import { toast } from 'sonner';
import { fmtMoney } from '@/lib/realtime';
import { PublicHeader, Footer } from '@/components/layout/PublicHeader';

export default function MenuPage() {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [activeCat, setActiveCat] = useState('all');
  const [loading, setLoading] = useState(true);
  const [activeItem, setActiveItem] = useState(null);
  const { add, items: cartItems, count, subtotal, setQty, remove } = useCart();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const tableQrToken = sp.get('table');

  useEffect(() => {
    Promise.all([api.get('/menu/categories'), api.get('/menu/items')])
      .then(([c, i]) => { setCategories(c.data || []); setItems(i.data || []); })
      .catch(() => toast.error('Failed to load menu'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tableQrToken) localStorage.setItem('cg_table_qr_token', tableQrToken);
  }, [tableQrToken]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return items.filter((it) =>
      (activeCat === 'all' || it.category_id === activeCat) &&
      (!s || it.name.toLowerCase().includes(s) || (it.description || '').toLowerCase().includes(s)),
    );
  }, [items, search, activeCat]);

  return (
    <div className="min-h-screen bg-background">
      <PublicHeader />
      {tableQrToken && (
        <div data-testid="menu-table-banner" className="bg-primary/10 border-b border-primary/20">
          <div className="mx-auto max-w-6xl px-4 py-2 text-sm text-primary sm:px-6">
            Ordering for your table · Dine-in mode active
          </div>
        </div>
      )}
      <div className="mx-auto grid max-w-6xl gap-6 px-4 py-6 sm:px-6 lg:grid-cols-[1fr_360px]">
        <div>
          <div className="sticky top-16 z-20 -mx-4 mb-4 border-b border-border bg-background/85 px-4 py-3 backdrop-blur supports-[backdrop-filter]:bg-background/70 sm:-mx-6 sm:px-6">
            <div className="flex items-center gap-3">
              <div className="relative w-full max-w-sm">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="menu-search-input" placeholder="Search dishes…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-10" />
              </div>
            </div>
            <ScrollArea className="mt-3 w-full whitespace-nowrap">
              <div className="flex gap-2 pb-2">
                <Button data-testid="menu-category-tab-all" size="sm" variant={activeCat === 'all' ? 'default' : 'secondary'} onClick={() => setActiveCat('all')}>All</Button>
                {categories.map((c) => (
                  <Button key={c.id} data-testid="menu-category-tab" size="sm" variant={activeCat === c.id ? 'default' : 'secondary'} onClick={() => setActiveCat(c.id)} className="shrink-0">
                    {c.name}
                  </Button>
                ))}
              </div>
            </ScrollArea>
          </div>

          {loading ? (
            <div className="grid gap-4 sm:grid-cols-2">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-72 animate-pulse rounded-xl bg-secondary" />
              ))}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2">
              {filtered.map((it) => (
                <Card key={it.id} data-testid="menu-item-card" className="group cursor-pointer overflow-hidden transition hover:border-primary hover:shadow-md" onClick={() => setActiveItem(it)}>
                  <AspectRatio ratio={16 / 10} className="overflow-hidden">
                    <img src={it.image_url} alt={it.name} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                  </AspectRatio>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <h3 className="font-semibold leading-tight group-hover:text-primary">{it.name}</h3>
                        <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{it.description}</p>
                        <div className="mt-2 flex flex-wrap gap-1">
                          {(it.tags || []).slice(0, 3).map((t) => (
                            <Badge key={t} variant="secondary" className="text-[10px] uppercase tracking-wide">{t}</Badge>
                          ))}
                        </div>
                      </div>
                      <span className="shrink-0 text-lg font-semibold tabular">{fmtMoney(it.price)}</span>
                    </div>
                    <div className="mt-4 flex items-center justify-between gap-2">
                      <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                        <Info className="h-3 w-3" /> Tap for details · ~{it.prep_time_minutes} min
                      </span>
                      <Button data-testid="menu-add-to-cart-button" size="sm" onClick={(e) => { e.stopPropagation(); add(it, 1); toast.success(`${it.name} added to cart`); }}>
                        <Plus className="mr-1 h-4 w-4" /> Add to cart
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {!filtered.length && <p className="text-sm text-muted-foreground">No items match your search.</p>}
            </div>
          )}
        </div>

        {/* Desktop cart panel */}
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <CartPanel onCheckout={() => nav('/checkout')} cartItems={cartItems} setQty={setQty} remove={remove} subtotal={subtotal} count={count} />
          </div>
        </aside>
      </div>

      {/* Mobile cart sheet */}
      <div className="fixed bottom-4 right-4 z-30 lg:hidden">
        <Sheet>
          <SheetTrigger asChild>
            <Button data-testid="menu-cart-sheet-trigger" size="lg" className="h-14 rounded-full px-5 shadow-xl">
              <ShoppingBag className="mr-2 h-5 w-5" />
              {count > 0 ? `${count} · ${fmtMoney(subtotal)}` : 'Your cart'}
            </Button>
          </SheetTrigger>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-hidden p-0">
            <SheetHeader className="px-4 pt-4"><SheetTitle>Your cart</SheetTitle></SheetHeader>
            <div className="flex max-h-[60vh] flex-col overflow-y-auto px-4">
              <CartList cartItems={cartItems} setQty={setQty} remove={remove} />
            </div>
            <SheetFooter className="border-t border-border bg-background p-4">
              <div className="flex w-full items-center justify-between">
                <span className="text-sm text-muted-foreground">Subtotal</span>
                <span className="text-lg font-semibold tabular">{fmtMoney(subtotal)}</span>
              </div>
              <Button data-testid="menu-checkout-button-mobile" className="w-full h-11" disabled={!count} onClick={() => nav('/checkout')}>Checkout <ChevronRight className="ml-1 h-4 w-4" /></Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>
      <MenuItemDetailDialog item={activeItem} onClose={() => setActiveItem(null)} onAdd={(it, qty) => { add(it, qty); toast.success(`${it.name} × ${qty} added to cart`); setActiveItem(null); }} />
      <Footer />
    </div>
  );
}

function MenuItemDetailDialog({ item, onClose, onAdd }) {
  const [qty, setQty] = useState(1);
  const [recipe, setRecipe] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => {
    if (!item) { setRecipe(null); setQty(1); return; }
    setQty(1);
    setLoading(true);
    api.get(`/menu/items/${item.id}/recipe`)
      .then((r) => setRecipe(r.data))
      .catch(() => setRecipe(null))
      .finally(() => setLoading(false));
  }, [item]);

  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="menu-item-dialog" className="max-w-3xl overflow-hidden p-0 sm:rounded-2xl">
        {item && (
          <>
            <div className="relative h-56 overflow-hidden sm:h-64">
              <img src={item.image_url} alt={item.name} className="h-full w-full object-cover" />
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                <DialogHeader className="space-y-1">
                  <DialogTitle className="font-display text-2xl text-white drop-shadow">{item.name}</DialogTitle>
                  <DialogDescription className="line-clamp-2 text-white/85">{item.description}</DialogDescription>
                </DialogHeader>
              </div>
            </div>
            <div className="max-h-[55vh] overflow-y-auto p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <span className="font-display text-3xl font-semibold text-primary tabular">{fmtMoney(item.price)}</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-3 py-1 text-xs text-secondary-foreground">
                  <Clock className="h-3.5 w-3.5" /> ~{item.prep_time_minutes} min
                </span>
              </div>

              {(item.tags || []).length > 0 && (
                <div className="mt-4">
                  <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground"><Tag className="h-3 w-3" /> Highlights</p>
                  <div className="flex flex-wrap gap-1">
                    {(item.tags || []).map((t) => (
                      <Badge key={t} variant="secondary" className="text-[10px] uppercase tracking-wide">{t}</Badge>
                    ))}
                  </div>
                </div>
              )}

              {(item.allergens || []).length > 0 && (
                <div className="mt-4">
                  <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-rose-700"><AlertCircle className="h-3 w-3" /> Allergens</p>
                  <div className="flex flex-wrap gap-1">
                    {(item.allergens || []).map((a) => (
                      <Badge key={a} className="border border-rose-200 bg-rose-50 text-[10px] uppercase tracking-wide text-rose-900">{a}</Badge>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-4">
                <p className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground"><Leaf className="h-3 w-3" /> What's inside</p>
                {loading ? (
                  <p className="text-sm text-muted-foreground">Loading ingredients…</p>
                ) : recipe && (recipe.ingredients || []).length > 0 ? (
                  <ul data-testid="menu-item-dialog-ingredients" className="grid grid-cols-2 gap-1.5 sm:grid-cols-3">
                    {recipe.ingredients.map((ing, i) => (
                      <li key={i} className="flex items-center gap-2 rounded border border-border bg-card px-2 py-1.5 text-xs">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span className="truncate">{ing.name}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-xs text-muted-foreground">Our chefs are still finalizing the ingredient list for this dish.</p>
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-border bg-muted/30 px-5 py-4">
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Quantity</span>
                <div className="inline-flex items-center overflow-hidden rounded-md border border-border">
                  <Button data-testid="menu-item-dialog-qty-minus" size="icon" variant="ghost" className="h-9 w-9 rounded-none" disabled={qty <= 1} onClick={() => setQty((q) => Math.max(1, q - 1))}><Minus className="h-4 w-4" /></Button>
                  <span className="w-10 text-center text-sm font-semibold tabular">{qty}</span>
                  <Button data-testid="menu-item-dialog-qty-plus" size="icon" variant="ghost" className="h-9 w-9 rounded-none" onClick={() => setQty((q) => q + 1)}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
              <Button data-testid="menu-item-dialog-add-button" className="min-w-[180px]" onClick={() => onAdd(item, qty)}>
                Add {qty} to cart · {fmtMoney(item.price * qty)}
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CartPanel({ onCheckout, cartItems, setQty, remove, subtotal, count }) {
  return (
    <Card>
      <div className="p-4">
        <h3 className="font-display text-lg font-semibold">Your cart</h3>
        <p className="text-xs text-muted-foreground">Add items from the menu</p>
      </div>
      <div className="border-t border-border">
        <ScrollArea className="max-h-[420px]">
          <div className="p-3">
            <CartList cartItems={cartItems} setQty={setQty} remove={remove} />
          </div>
        </ScrollArea>
      </div>
      <div className="border-t border-border p-4">
        <div className="mb-3 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">Subtotal</span>
          <span className="text-lg font-semibold tabular">{fmtMoney(subtotal)}</span>
        </div>
        <Button data-testid="menu-checkout-button" className="w-full h-11" disabled={!count} onClick={onCheckout}>Checkout</Button>
      </div>
    </Card>
  );
}

function CartList({ cartItems, setQty, remove }) {
  if (!cartItems.length) return <p className="py-6 text-center text-sm text-muted-foreground">Cart is empty</p>;
  return (
    <ul className="flex flex-col gap-3">
      {cartItems.map((i) => (
        <li key={i.menu_item_id} data-testid="cart-line-item" className="flex items-start gap-3 rounded-lg border border-border bg-card p-2">
          <img src={i.image_url} alt={i.name} className="h-14 w-14 rounded-md object-cover" />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{i.name}</p>
            <p className="text-xs text-muted-foreground">{fmtMoney(i.price)} each</p>
            <div className="mt-2 flex items-center gap-2">
              <Button data-testid="cart-qty-decrement-button" variant="secondary" size="icon" className="h-7 w-7" onClick={() => setQty(i.menu_item_id, i.quantity - 1)}><Minus className="h-3 w-3" /></Button>
              <span className="w-6 text-center text-sm tabular">{i.quantity}</span>
              <Button data-testid="cart-qty-increment-button" variant="secondary" size="icon" className="h-7 w-7" onClick={() => setQty(i.menu_item_id, i.quantity + 1)}><Plus className="h-3 w-3" /></Button>
              <Button data-testid="cart-remove-button" variant="ghost" size="icon" className="ml-auto h-7 w-7 text-muted-foreground" onClick={() => remove(i.menu_item_id)}><Trash2 className="h-3 w-3" /></Button>
            </div>
          </div>
          <span className="shrink-0 text-sm font-semibold tabular">{fmtMoney(i.price * i.quantity)}</span>
        </li>
      ))}
    </ul>
  );
}
